import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { i18nStore } from '@store/I18nStore';
import { isFxLite } from '@utils/fxLite';

gsap.registerPlugin(ScrollTrigger);

const t = (key: string) => i18nStore.t(key);
/** Dictionary text that ends up inside an HTML string — escaped so a dictionary value can never inject markup. */
const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const th = (key: string) => escapeHtml(t(key));

gsap.registerPlugin(ScrollTrigger);

const docHidden = () => document.hidden;
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
/** Phones: lighter canvases (fewer particles, 30 fps, dpr ≤ 1.5, no glow/shadow passes). */
const LITE_FRAME_MS = 1000 / 30;
const canvasDpr = (lite: boolean) => Math.min(devicePixelRatio || 1, lite ? 1.5 : 2);

/**
 * Start the landing at the top on (re)load — unless the URL names a section
 * (`/#pricing`): then LandingPage scrolls there once the sections are mounted,
 * and forcing the top here would send the visitor "somewhere" on the page.
 */
export function initScrollTop(): () => void {
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  const hasSectionHash = () => window.location.hash.length > 1;
  // `behavior: 'instant'` — the landing sets `scroll-behavior: smooth` on <html>,
  // a plain scrollTo(0, 0) would animate.
  const toTop = () => {
    if (hasSectionHash()) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  };
  toTop();
  const onLoad = () => {
    toTop();
    requestAnimationFrame(toTop);
  };
  window.addEventListener('load', onLoad);
  window.addEventListener('pageshow', toTop);
  return () => {
    window.removeEventListener('load', onLoad);
    window.removeEventListener('pageshow', toTop);
  };
}

export function initBgFX(canvas: HTMLCanvasElement): () => void {
  // Phones render a static gradient instead (see FxBackground) — no loop at all.
  if (isFxLite()) return () => {};
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};
  let w = 0;
  let h = 0;
  let dpr = 1;
  let raf = 0;
  type Node = { x: number; y: number; vx: number; vy: number; r: number; c: string; tw: number; ts: number };
  type Orb = { x: number; y: number; vx: number; vy: number; r: number; c: string };
  type Shape = { x: number; y: number; vx: number; vy: number; a: number; va: number; s: number; k: string; c: string; tw: number; ts: number };
  let nodes: Node[] = [];
  let orbs: Orb[] = [];
  let shapes: Shape[] = [];
  const C = ['#2f6bff', '#6d8cff', '#5b8cff', '#22d3ee'];
  const KINDS = ['ring', 'tri', 'square', 'hex', 'plus'];

  const drawShape = (sh: Shape, al: number) => {
    ctx.save();
    ctx.translate(sh.x, sh.y);
    ctx.rotate(sh.a);
    ctx.globalAlpha = al;
    ctx.strokeStyle = sh.c;
    ctx.lineWidth = 1.4;
    ctx.lineJoin = 'round';
    const s = sh.s;
    ctx.beginPath();
    if (sh.k === 'ring') ctx.arc(0, 0, s * 0.55, 0, 6.28);
    else if (sh.k === 'tri') {
      for (let i = 0; i < 3; i++) {
        const an = -1.5708 + i * 2.0944;
        const px = Math.cos(an) * s * 0.65;
        const py = Math.sin(an) * s * 0.65;
        i ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
      }
      ctx.closePath();
    } else if (sh.k === 'square') ctx.rect(-s * 0.45, -s * 0.45, s * 0.9, s * 0.9);
    else if (sh.k === 'hex') {
      for (let i = 0; i < 6; i++) {
        const an = i * 1.0472;
        const px = Math.cos(an) * s * 0.55;
        const py = Math.sin(an) * s * 0.55;
        i ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
      }
      ctx.closePath();
    } else {
      ctx.moveTo(-s * 0.45, 0);
      ctx.lineTo(s * 0.45, 0);
      ctx.moveTo(0, -s * 0.45);
      ctx.lineTo(0, s * 0.45);
    }
    ctx.stroke();
    ctx.restore();
  };

  const resize = () => {
    dpr = Math.min(devicePixelRatio || 1, 2);
    w = canvas.clientWidth;
    h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const n = Math.min(75, Math.floor((w * h) / 24000));
    nodes = Array.from({ length: n }, () => ({
      x: Math.random() * w, y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.26, vy: (Math.random() - 0.5) * 0.26,
      r: Math.random() * 1.7 + 0.8, c: C[Math.floor(Math.random() * C.length)],
      tw: Math.random() * 6.28, ts: 0.5 + Math.random(),
    }));
    orbs = Array.from({ length: 5 }, (_, i) => ({
      x: Math.random() * w, y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.18, vy: (Math.random() - 0.5) * 0.18,
      r: 120 + Math.random() * 140, c: C[i % C.length],
    }));
    shapes = Array.from({ length: Math.min(16, Math.floor(w / 100)) }, () => ({
      x: Math.random() * w, y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.16, vy: (Math.random() - 0.5) * 0.16,
      a: Math.random() * 6.28, va: (Math.random() - 0.5) * 0.005,
      s: 16 + Math.random() * 40,
      k: KINDS[Math.floor(Math.random() * KINDS.length)],
      c: C[Math.floor(Math.random() * C.length)],
      tw: Math.random() * 6.28, ts: 0.25 + Math.random() * 0.5,
    }));
  };

  const frame = () => {
    raf = requestAnimationFrame(frame);
    if (docHidden()) return;
    const t = performance.now() * 0.001;
    ctx.clearRect(0, 0, w, h);
    for (const o of orbs) {
      o.x += o.vx;
      o.y += o.vy;
      if (o.x < -160 || o.x > w + 160) o.vx *= -1;
      if (o.y < -160 || o.y > h + 160) o.vy *= -1;
      const g = ctx.createRadialGradient(o.x, o.y, 0, o.x, o.y, o.r);
      g.addColorStop(0, o.c + '26');
      g.addColorStop(1, o.c + '00');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(o.x, o.y, o.r, 0, 6.28);
      ctx.fill();
    }
    for (const sh of shapes) {
      sh.x += sh.vx;
      sh.y += sh.vy;
      sh.a += sh.va;
      if (sh.x < -70 || sh.x > w + 70) sh.vx *= -1;
      if (sh.y < -70 || sh.y > h + 70) sh.vy *= -1;
      drawShape(sh, 0.07 + 0.07 * Math.sin(t * sh.ts + sh.tw));
    }
    ctx.globalAlpha = 1;
    for (const p of nodes) {
      p.x += p.vx;
      p.y += p.vy;
      if (p.x < 0 || p.x > w) p.vx *= -1;
      if (p.y < 0 || p.y > h) p.vy *= -1;
      const a = 0.5 + 0.5 * Math.sin(t * p.ts + p.tw);
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, 6.28);
      ctx.fillStyle = p.c;
      ctx.globalAlpha = 0.55 * a;
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i];
        const b = nodes[j];
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const d = Math.hypot(dx, dy);
        if (d < 135) {
          ctx.globalAlpha = (1 - d / 135) * 0.26;
          ctx.strokeStyle = '#5b8cff';
          ctx.lineWidth = 0.7;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
    }
    ctx.globalAlpha = 1;
  };

  window.addEventListener('resize', resize);
  resize();
  frame();
  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', resize);
  };
}

export function initConstellation(canvas: HTMLCanvasElement, hero: HTMLElement | null): () => void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};
  const lite = isFxLite();
  let w = 0;
  let h = 0;
  let dpr = 1;
  let raf = 0;
  let lastFrame = 0;
  type Part = {
    x: number; y: number; vx: number; vy: number; bx: number; by: number;
    r: number; c: string; tw: number; ts: number; grab?: number;
  };
  let parts: Part[] = [];
  let visible = true;
  const mouse = { x: -999, y: -999 };
  const COLORS = ['#0059E1', '#3b82f6', '#6366f1', '#22d3ee'];
  const R = 190;
  const RING = 46;

  const resize = () => {
    dpr = canvasDpr(lite);
    w = canvas.clientWidth;
    h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const target = lite
      ? Math.max(22, Math.min(36, Math.floor((w * h) / 16000)))
      : Math.max(34, Math.min(95, Math.floor((w * h) / 10000)));
    parts = Array.from({ length: target }, () => {
      const bx = (Math.random() - 0.5) * 0.28;
      const by = (Math.random() - 0.5) * 0.28;
      return {
        x: Math.random() * w, y: Math.random() * h, vx: bx, vy: by, bx, by,
        r: Math.random() * 1.6 + 0.6, c: COLORS[Math.floor(Math.random() * COLORS.length)],
        tw: Math.random() * 6.28, ts: 0.6 + Math.random() * 1.4,
      };
    });
  };

  const onMouseMove = (e: MouseEvent) => {
    const r = canvas.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    if (x >= 0 && x <= r.width && y >= 0 && y <= r.height) {
      mouse.x = x;
      mouse.y = y;
    } else {
      mouse.x = -999;
      mouse.y = -999;
    }
  };

  const onMouseLeave = () => {
    mouse.x = -999;
    mouse.y = -999;
  };

  const setPointer = (clientX: number, clientY: number) => {
    const r = canvas.getBoundingClientRect();
    const x = clientX - r.left;
    const y = clientY - r.top;
    if (x >= 0 && x <= r.width && y >= 0 && y <= r.height) {
      mouse.x = x;
      mouse.y = y;
    } else {
      mouse.x = -999;
      mouse.y = -999;
    }
  };

  const onTouchMove = (e: TouchEvent) => {
    const t = e.touches[0];
    if (!t) return;
    setPointer(t.clientX, t.clientY);
  };

  const onTouchEnd = () => {
    mouse.x = -999;
    mouse.y = -999;
  };

  const step = () => {
    raf = requestAnimationFrame(step);
    if (docHidden() || !visible) return;
    const now = performance.now();
    if (lite && now - lastFrame < LITE_FRAME_MS) return;
    lastFrame = now;
    const t = now * 0.001;
    ctx.clearRect(0, 0, w, h);
    const active = !lite && mouse.x > -500;
    const RING = 46;
    const linkDist = lite ? 110 : 120;
    for (const p of parts) {
      const dx = mouse.x - p.x;
      const dy = mouse.y - p.y;
      const d = Math.hypot(dx, dy) || 1;
      if (active && d < R) {
        const f = 1 - d / R;
        if (d > RING) {
          p.vx += (dx / d) * f * 0.22;
          p.vy += (dy / d) * f * 0.22;
        } else {
          p.vx -= (dx / d) * 0.35;
          p.vy -= (dy / d) * 0.35;
        }
        p.vx *= 0.9;
        p.vy *= 0.9;
        p.grab = f;
      } else {
        p.vx += (p.bx - p.vx) * 0.04;
        p.vy += (p.by - p.vy) * 0.04;
        p.grab = (p.grab || 0) * 0.92;
      }
      p.x += p.vx;
      p.y += p.vy;
      if (p.x < 0 || p.x > w) { p.vx *= -1; p.bx *= -1; }
      if (p.y < 0 || p.y > h) { p.vy *= -1; p.by *= -1; }
      p.x = Math.max(0, Math.min(w, p.x));
      p.y = Math.max(0, Math.min(h, p.y));
      const twinkle = 0.55 + 0.45 * Math.sin(t * p.ts + p.tw);
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, 6.28);
      ctx.fillStyle = p.c;
      ctx.globalAlpha = Math.min(1, 0.9 * twinkle + (p.grab || 0) * 0.35);
      ctx.fill();
    }
    for (let i = 0; i < parts.length; i++) {
      for (let j = i + 1; j < parts.length; j++) {
        const a = parts[i];
        const b = parts[j];
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const d = Math.hypot(dx, dy);
        if (d < linkDist) {
          ctx.globalAlpha = (1 - d / linkDist) * 0.35;
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
      if (!active) continue;
      const dmx = parts[i].x - mouse.x;
      const dmy = parts[i].y - mouse.y;
      const dm = Math.hypot(dmx, dmy);
      if (dm < R) {
        ctx.globalAlpha = (1 - dm / R) * 0.45;
        ctx.strokeStyle = '#6aa3ff';
        ctx.lineWidth = 0.7;
        ctx.beginPath();
        ctx.moveTo(parts[i].x, parts[i].y);
        ctx.lineTo(mouse.x, mouse.y);
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
  };

  const touchTarget = hero || canvas;
  let io: IntersectionObserver | undefined;
  if (hero && 'IntersectionObserver' in window) {
    io = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; }, { threshold: 0.05 });
    io.observe(hero);
  }
  window.addEventListener('resize', resize);
  if (!lite) {
    window.addEventListener('mousemove', onMouseMove);
    touchTarget.addEventListener('mouseleave', onMouseLeave);
    touchTarget.addEventListener('touchstart', onTouchMove, { passive: true });
    touchTarget.addEventListener('touchmove', onTouchMove, { passive: true });
    touchTarget.addEventListener('touchend', onTouchEnd, { passive: true });
  }
  resize();
  step();
  return () => {
    cancelAnimationFrame(raf);
    io?.disconnect();
    window.removeEventListener('resize', resize);
    window.removeEventListener('mousemove', onMouseMove);
    touchTarget.removeEventListener('mouseleave', onMouseLeave);
    touchTarget.removeEventListener('touchstart', onTouchMove);
    touchTarget.removeEventListener('touchmove', onTouchMove);
    touchTarget.removeEventListener('touchend', onTouchEnd);
  };
}

export function initCtaFlow(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};
  const lite = isFxLite();
  let w = 0;
  let h = 0;
  let dpr = 1;
  let raf = 0;
  let t = 0;
  let lastFrame = 0;
  let visible = true;
  const stepX = lite ? 8 : 6;
  const waves = [
    { amp: 18, len: 0.012, sp: 0.7, off: 0.34, col: 'rgba(0,89,225,.55)', lw: 2 },
    { amp: 26, len: 0.009, sp: -0.5, off: 0.5, col: 'rgba(59,130,246,.45)', lw: 1.6 },
    { amp: 14, len: 0.016, sp: 1.0, off: 0.64, col: 'rgba(99,102,241,.4)', lw: 1.4 },
    { amp: 30, len: 0.007, sp: 0.35, off: 0.8, col: 'rgba(34,211,238,.3)', lw: 1.2 },
  ];

  const resize = () => {
    dpr = canvasDpr(lite);
    w = canvas.clientWidth;
    h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  const frame = () => {
    raf = requestAnimationFrame(frame);
    if (docHidden() || !visible) return;
    const now = performance.now();
    if (lite && now - lastFrame < LITE_FRAME_MS) return;
    lastFrame = now;
    ctx.clearRect(0, 0, w, h);
    t += lite ? 0.032 : 0.016;
    for (const wv of waves) {
      ctx.beginPath();
      for (let x = 0; x <= w; x += stepX) {
        const y = h * wv.off
          + Math.sin(x * wv.len + t * wv.sp * 3) * wv.amp
          + Math.sin(x * wv.len * 0.5 - t * wv.sp) * wv.amp * 0.5;
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      if (lite) {
        // shadowBlur is a CPU blur of the whole stroke every frame — on phones fake the
        // glow with a second wide translucent stroke of the same path.
        ctx.globalAlpha = 0.28;
        ctx.strokeStyle = wv.col;
        ctx.lineWidth = wv.lw + 7;
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.lineWidth = wv.lw;
        ctx.stroke();
      } else {
        ctx.strokeStyle = wv.col;
        ctx.lineWidth = wv.lw;
        ctx.shadowBlur = 12;
        ctx.shadowColor = wv.col;
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
    }
    const wv = waves[1];
    for (let k = 0; k < 6; k++) {
      const x = ((t * 60 + (k * w) / 6) % (w + 40)) - 20;
      const y = h * wv.off + Math.sin(x * wv.len + t * wv.sp * 3) * wv.amp + Math.sin(x * wv.len * 0.5 - t * wv.sp) * wv.amp * 0.5;
      ctx.beginPath();
      ctx.arc(x, y, 2.2, 0, 6.28);
      ctx.fillStyle = 'rgba(147,197,253,.9)';
      ctx.fill();
    }
  };

  let io: IntersectionObserver | undefined;
  const section = canvas.closest('section');
  if (section && 'IntersectionObserver' in window) {
    io = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; }, { threshold: 0.08 });
    io.observe(section);
  }

  window.addEventListener('resize', resize);
  resize();
  frame();
  return () => {
    cancelAnimationFrame(raf);
    io?.disconnect();
    window.removeEventListener('resize', resize);
  };
}

export function initGsapAnimations(root: HTMLElement): () => void {
  const ctx = gsap.context(() => {
    if (reducedMotion()) {
      gsap.set(root.querySelectorAll('[data-hero], .reveal, .reveal-l, .reveal-r, .reveal-s'), {
        opacity: 1, x: 0, y: 0, scale: 1,
      });
      return;
    }

    gsap.from(root.querySelectorAll('[data-hero]'), {
      y: 40, opacity: 0, duration: 1, ease: 'power3.out', stagger: 0.13, delay: 0.15,
    });

    const singles = gsap.utils.toArray<HTMLElement>('.reveal', root).filter(
      (el) => !el.closest('[data-stagger]'),
    );
    singles.forEach((el) => {
      gsap.to(el, {
        y: 0, opacity: 1, duration: 0.85, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 90%' },
      });
    });

    gsap.utils.toArray<HTMLElement>('.reveal-l', root).forEach((el) => {
      gsap.to(el, {
        x: 0, opacity: 1, duration: 0.9, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 88%' },
      });
    });

    gsap.utils.toArray<HTMLElement>('.reveal-r', root).forEach((el) => {
      gsap.to(el, {
        x: 0, opacity: 1, duration: 0.9, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 88%' },
      });
    });

    gsap.utils.toArray<HTMLElement>('[data-stagger]', root).forEach((group) => {
      const items = group.querySelectorAll('.reveal');
      gsap.to(items, {
        y: 0, opacity: 1, scale: 1, duration: 0.7, ease: 'back.out(1.4)', stagger: 0.08,
        scrollTrigger: { trigger: group, start: 'top 85%' },
      });
    });

    if (!isFxLite()) {
      // Blob parallax + background hue tween drive `filter: hue-rotate` on the
      // whole fx layer — desktop only; phones have a static gradient backdrop.
      gsap.utils.toArray<HTMLElement>('.blob', root).forEach((b, i) => {
        gsap.to(b, {
          yPercent: i % 2 ? -16 : 16, ease: 'none',
          scrollTrigger: { trigger: document.body, start: 'top top', end: 'bottom bottom', scrub: 1 },
        });
      });

      const hueMap: [string, number][] = [
        ['#hero', 0], ['#immunity', -26], ['#features', -34],
        ['#how', 12], ['#pricing', -22], ['#trial', -8], ['#compare', 6],
        ['#privacy', -18], ['#faq', 8], ['#cta', -30],
      ];
      hueMap.forEach(([sel, hue]) => {
        const el = root.querySelector(sel);
        if (!el) return;
        const apply = () => gsap.to(document.documentElement, {
          '--bgHue': `${hue}deg`, duration: 1.1, ease: 'sine.out', overwrite: 'auto',
        });
        ScrollTrigger.create({
          trigger: el, start: 'top 55%', end: 'bottom 45%', onEnter: apply, onEnterBack: apply,
        });
      });
    }

    const typedCmd = root.querySelector('#typedCmd');
    const cmdSuggest = root.querySelector('#cmdSuggest');
    const text = `/${t('help.articles.templates.hint_demo_shortcut_hello')}`;
    let typedRan = false;
    if (typedCmd) {
      ScrollTrigger.create({
        trigger: '#features', start: 'top 70%',
        onEnter: async () => {
          if (typedRan) return;
          typedRan = true;
          typedCmd.textContent = '';
          for (let i = 0; i < text.length; i++) {
            typedCmd.textContent += text[i];
            await new Promise((r) => setTimeout(r, 90));
            if (text[i] === '/' && cmdSuggest) (cmdSuggest as HTMLElement).style.opacity = '1';
          }
        },
      });
    }

    const chartLine = root.querySelector('#chartLine') as SVGPathElement | null;
    const chartFill = root.querySelector('#chartFill');
    if (chartLine && chartFill) {
      const len = chartLine.getTotalLength();
      chartLine.style.strokeDasharray = String(len);
      chartLine.style.strokeDashoffset = String(len);
      ScrollTrigger.create({
        trigger: '#loadChart', start: 'top 80%',
        onEnter: () => {
          gsap.to(chartLine, { strokeDashoffset: 0, duration: 1.6, ease: 'power2.out' });
          gsap.to(chartFill, { opacity: 1, duration: 1, delay: 0.6 });
        },
      });
    }

    root.querySelectorAll('[data-count]').forEach((el) => {
      ScrollTrigger.create({
        trigger: el, start: 'top 92%', once: true,
        onEnter: () => {
          const htmlEl = el as HTMLElement;
          const raw = htmlEl.dataset.count || '0';
          const target = parseFloat(raw);
          const suf = htmlEl.dataset.suffix || '';
          const dec = (raw.split('.')[1] || '').length;
          const obj = { v: 0 };
          gsap.to(obj, {
            v: target, duration: 1.6, ease: 'power2.out',
            onUpdate: () => {
              const parts = obj.v.toFixed(dec).split('.');
              parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
              htmlEl.textContent = parts.join('.') + suf;
            },
          });
        },
      });
    });
  }, root);

  return () => ctx.revert();
}

export function initBentoTilt(root: HTMLElement): () => void {
  const handlers: Array<{ el: Element; fn: EventListener }> = [];
  root.querySelectorAll('.tilt').forEach((card) => {
    const fn: EventListener = (evt) => {
      const e = evt as MouseEvent;
      const r = (card as HTMLElement).getBoundingClientRect();
      (card as HTMLElement).style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
      (card as HTMLElement).style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
    };
    card.addEventListener('mousemove', fn);
    handlers.push({ el: card, fn });
  });
  return () => handlers.forEach(({ el, fn }) => el.removeEventListener('mousemove', fn));
}

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const node = (html: string) => {
  const d = document.createElement('div');
  d.innerHTML = html.trim();
  return d.firstElementChild as HTMLElement;
};

const end = (el: HTMLElement) => {
  gsap.to(el, { scrollTop: el.scrollHeight, duration: 0.4, ease: 'power2.out', overwrite: true });
};

export function initChatDemo(root: HTMLElement): () => void {
  const wBody = root.querySelector('#chatBody2') as HTMLElement | null;
  const greeting = root.querySelector('#chatGreeting') as HTMLElement | null;
  const openBtn = root.querySelector('#chatOpenBtn');
  const oBody = root.querySelector('#opBody') as HTMLElement | null;
  const oQuick = root.querySelector('#opQuick') as HTMLElement | null;
  const oSearch = root.querySelector('#opSearch');
  const oSuggest = root.querySelector('#opSuggest');
  const oInput = root.querySelector('#opInput');
  const oPlaceholder = root.querySelector('#opPlaceholder') as HTMLElement | null;
  if (!wBody || !oBody) return () => {};

  let started = false;
  let clock = 797;
  const tnow = () => {
    const h = String(Math.floor(clock / 60) % 24).padStart(2, '0');
    const m = String(clock % 60).padStart(2, '0');
    return `${h}:${m}`;
  };

  const wMsg = (role: string, text: string) => {
    const out = role === 'client';
    const wrap = node(`<div class="flex flex-col ${out ? 'items-end' : 'items-start'}"></div>`);
    const b = node(`<div class="max-w-[88%] px-3.5 py-2.5 rounded-2xl text-sm leading-snug ${out ? 'text-white rounded-br-sm' : 'bg-white/8 text-slate-100 rounded-bl-sm'}"></div>`);
    if (out) b.style.background = '#37597a';
    b.textContent = text;
    wrap.appendChild(b);
    wrap.appendChild(node(`<div class="text-[10px] text-slate-500 mt-0.5">${tnow()}</div>`));
    wBody.appendChild(wrap);
    gsap.fromTo(b, { opacity: 0, y: 8, scale: 0.96 }, { opacity: 1, y: 0, scale: 1, duration: 0.4, ease: 'back.out(1.5)' });
    end(wBody);
  };

  const wTyping = (role: string) => {
    const out = role === 'client';
    const wrap = node(`<div class="flex ${out ? 'justify-end' : 'justify-start'}"></div>`);
    const bub = node('<div class="rounded-2xl px-4 py-3 flex gap-1"></div>');
    bub.style.background = out ? '#37597a' : 'rgba(255,255,255,.08)';
    bub.innerHTML = '<span class="w-1.5 h-1.5 bg-slate-300 rounded-full typing-dot"></span><span class="w-1.5 h-1.5 bg-slate-300 rounded-full typing-dot"></span><span class="w-1.5 h-1.5 bg-slate-300 rounded-full typing-dot"></span>';
    wrap.appendChild(bub);
    wBody.appendChild(wrap);
    end(wBody);
    return wrap;
  };

  const oMsg = (role: string, text: string) => {
    if (role === 'operator') {
      const wrap = node('<div class="flex items-end justify-end gap-2"></div>');
      const col = node('<div class="max-w-[80%] rounded-2xl rounded-br-sm bg-[#1a2150] border border-brand/25 px-3.5 py-2.5"></div>');
      col.appendChild(node('<div class="text-[11px] text-slate-400 mb-0.5">Daria</div>'));
      const b = node('<div class="text-sm text-white leading-snug"></div>');
      b.textContent = text;
      col.appendChild(b);
      col.appendChild(node(`<div class="text-[10px] text-slate-400 text-right mt-1">${tnow()}</div>`));
      wrap.appendChild(col);
      wrap.appendChild(node('<div class="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center text-[11px] font-bold text-white shrink-0">D</div>'));
      oBody.appendChild(wrap);
      gsap.fromTo(col, { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.4, ease: 'back.out(1.5)' });
    } else {
      const wrap = node('<div class="flex items-end gap-2"></div>');
      wrap.appendChild(node('<div class="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 grid place-items-center text-[11px] font-bold text-white shrink-0">C</div>'));
      const col = node('<div class="max-w-[80%] rounded-2xl rounded-bl-sm bg-[#171c39] border border-white/5 px-3.5 py-2.5"></div>');
      col.appendChild(node('<div class="text-[11px] text-slate-500 mb-0.5">Chat #665</div>'));
      const b = node('<div class="text-sm text-white leading-snug"></div>');
      b.textContent = text;
      col.appendChild(b);
      col.appendChild(node(`<div class="text-[10px] text-slate-500 text-right mt-1">${tnow()}</div>`));
      wrap.appendChild(col);
      oBody.appendChild(wrap);
      gsap.fromTo(col, { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.4, ease: 'back.out(1.5)' });
    }
    end(oBody);
  };

  const oSys = (text: string) => {
    const e = node(`<div class="text-center text-[11px] text-slate-500 py-0.5">${escapeHtml(text)}</div>`);
    e.style.opacity = '0';
    oBody.appendChild(e);
    gsap.to(e, { opacity: 1, duration: 0.4 });
    end(oBody);
  };

  const oTyping = () => {
    const wrap = node('<div class="flex items-end gap-2"></div>');
    wrap.appendChild(node('<div class="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 grid place-items-center text-[11px] font-bold text-white shrink-0">C</div>'));
    wrap.appendChild(node('<div class="bg-[#171c39] border border-white/5 rounded-2xl px-4 py-3 flex gap-1"><span class="w-1.5 h-1.5 bg-slate-300 rounded-full typing-dot"></span><span class="w-1.5 h-1.5 bg-slate-300 rounded-full typing-dot"></span><span class="w-1.5 h-1.5 bg-slate-300 rounded-full typing-dot"></span></div>'));
    oBody.appendChild(wrap);
    end(oBody);
    return wrap;
  };

  const typeInto = async (elm: HTMLElement, txt: string, sp = 45) => {
    elm.textContent = '';
    for (const c of txt) {
      elm.textContent += c;
      await wait(sp);
    }
  };

  const operatorReply = async (code: string, hint: string, reply: string) => {
    if (!oQuick || !oSuggest || !oInput) return;
    oQuick.style.opacity = '1';
    oQuick.style.transform = 'translateY(0)';
    oSuggest.innerHTML = '';
    ([[code, hint, true], ['/привет', t('demo.phrases.greeting'), false], [`/${t('help.articles.templates.hint_demo_shortcut_pause')}`, t('demo.messages.checking_info'), false]] as const).forEach(([c, txt, hot]) => {
      oSuggest.appendChild(node(`<div class="px-2.5 py-2 rounded-lg ${hot ? 'bg-brand/15 border border-brand/30' : ''}"><div class="text-xs font-semibold ${hot ? 'text-brand2' : 'text-slate-300'}">${escapeHtml(c)}</div><div class="text-[11px] text-slate-500 truncate">${escapeHtml(txt)}</div></div>`));
    });
    if (oSearch) await typeInto(oSearch as HTMLElement, code, 55);
    await wait(650);
    oQuick.style.opacity = '0';
    oQuick.style.transform = 'translateY(8px)';
    if (oSearch) oSearch.textContent = t('templates.search.shortcut_phrase_section');
    if (oPlaceholder) oPlaceholder.style.display = 'none';
    await typeInto(oInput as HTMLElement, code, 45);
    await wait(350);
    oInput.textContent = '';
    if (oPlaceholder) oPlaceholder.style.display = '';
    oMsg('operator', reply);
    wMsg('operator', reply);
  };

  const rating = () => {
    const box = node('<div class="mt-1 rounded-2xl border border-white/10 bg-white/[.04] p-4 text-center"></div>');
    box.innerHTML = `<div class="text-sm font-semibold mb-2">${th('widget.client.rate_dialog')}</div><div class="flex justify-center gap-1.5" id="stars5"></div><div id="rThanks" class="text-xs text-emerald-400 mt-2 opacity-0">${th('widget.client.rated_helpful')}</div>`;
    box.style.opacity = '0';
    wBody.appendChild(box);
    gsap.to(box, { opacity: 1, duration: 0.5 });
    end(wBody);
    const stars = box.querySelector('#stars5');
    if (stars) {
      for (let i = 0; i < 5; i++) {
        stars.appendChild(node('<svg viewBox="0 0 24 24" class="w-7 h-7" style="fill:#334155;stroke:none"><path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9 6.8 19l1-5.8L3.5 9.2l5.9-.9z"/></svg>'));
      }
      setTimeout(async () => {
        const arr = stars.querySelectorAll('svg');
        for (let i = 0; i < 5; i++) {
          (arr[i] as SVGElement).style.fill = '#fbbf24';
          gsap.fromTo(arr[i], { scale: 0.5 }, { scale: 1, duration: 0.3, ease: 'back.out(2.2)' });
          await wait(220);
        }
        const thanks = box.querySelector('#rThanks');
        if (thanks) gsap.to(thanks, { opacity: 1, duration: 0.5 });
        end(wBody);
        oSys(t('chat.closed_with_rating').replace('{rating}', '5').replace('{max}', '5'));
      }, 700);
    }
  };

  const clientSays = async (text: string) => {
    let wt = wTyping('client');
    await wait(950);
    wt.remove();
    clock += 1;
    wMsg('client', text);
    const ot = oTyping();
    await wait(350);
    ot.remove();
    oMsg('client', text);
  };

  const dialogue = async () => {
    oSys(`${t('demo.operator_joined')} · ${tnow()}`);
    await wait(550);
    await clientSays(t('demo.messages.hello'));
    await wait(650);
    let wt = wTyping('operator');
    await operatorReply(`/${t('billing.payment.title').toLowerCase()}`, t('billing.methods.accepted'), t('landing.faq.one_console_answer'));
    wt.remove();
    await wait(900);
    await clientSays(t('landing.faq.telegram_question'));
    await wait(600);
    wt = wTyping('operator');
    await operatorReply('/телеграм', t('landing.features.site_and_telegram_one_console'), t('landing.faq.one_console_answer'));
    wt.remove();
    await wait(850);
    await clientSays(t('demo.messages.thanks_ordering'));
    await wait(500);
    clock += 1;
    wMsg('operator', t('demo.phrases.goodbye'));
    oMsg('operator', t('demo.phrases.goodbye'));
    await wait(800);
    rating();
  };

  const openChat = () => {
    if (started) return;
    started = true;
    if (greeting) {
      gsap.to(greeting, {
        opacity: 0, scale: 0.96, duration: 0.45, ease: 'power2.out',
        onComplete: () => { greeting.style.display = 'none'; },
      });
    }
    setTimeout(dialogue, 450);
  };

  const onOpenClick = () => openChat();
  openBtn?.addEventListener('click', onOpenClick);

  const ctx = gsap.context(() => {
    ScrollTrigger.create({
      trigger: '#immunity', start: 'top 60%', once: true,
      onEnter: () => setTimeout(openChat, 1400),
    });
  }, root);

  return () => {
    openBtn?.removeEventListener('click', onOpenClick);
    ctx.revert();
  };
}
